import { company } from '@awpmarine/core/company';
import { AwpMarineLogo } from '@awpmarine/ui/components/logos';
import { Link } from '@tanstack/react-router';

import { SocialLinks } from '@/components/header';
import { LanguageMenu } from '@/components/language';
import { NewsletterForm } from '@/components/newsletter-form';
import Iso9001Img from '@/images/marketing/iso-9001-badge.jpg?badge';
import Iso45001Img from '@/images/marketing/iso-45001-badge.png?badge';
import { m } from '@/paraglide/messages';
import type { MessageFunction } from '@/paraglide/runtime';
import type { FileRouteTypes } from '@/routeTree.gen';

const year = new Date().getFullYear();

type Item =
  | { label: MessageFunction; to: Exclude<FileRouteTypes['to'], '/legal/$slug'>; hash?: string }
  | { label: MessageFunction; to: '/legal/$slug'; slug: string };

// Message functions, read when the footer renders so the text follows the page's locale.
const columns: { title: MessageFunction; items: Item[] }[] = [
  {
    title: m.footer_services,
    items: [
      { label: m.footer_inspections, to: '/services', hash: 'vessel-inspections' },
      { label: m.footer_surveys, to: '/services', hash: 'surveys-consultancy' },
      { label: m.footer_audits, to: '/services', hash: 'audits-assessments' },
      { label: m.footer_where_we_work, to: '/services', hash: 'where-we-work' },
      { label: m.footer_all_services, to: '/services' },
    ],
  },
  {
    title: m.footer_company,
    items: [
      { label: m.footer_about, to: '/about' },
      { label: m.footer_team, to: '/team' },
      { label: m.footer_news, to: '/news' },
      { label: m.footer_careers, to: '/careers' },
    ],
  },
  {
    title: m.footer_legal,
    items: [
      { label: m.footer_terms, to: '/legal/$slug', slug: 'terms' },
      { label: m.footer_privacy, to: '/legal/$slug', slug: 'privacy' },
      { label: m.footer_subprocessors, to: '/legal/$slug', slug: 'subprocessors' },
      { label: m.footer_policies, to: '/legal' },
    ],
  },
];

export function Footer() {
  return (
    <footer className='bg-navy text-white'>
      <div className='mx-auto flex max-w-310 flex-wrap items-center justify-between gap-8 border-b border-[#2c3f5c] px-5 pt-14 pb-12 sm:px-8 sm:pt-16'>
        <div className='max-w-130'>
          <h2 className='mb-2 text-[28px] leading-tight font-bold'>{m.footer_newsletter_title()}</h2>
          <p className='text-on-navy'>{m.footer_newsletter_text()}</p>
        </div>
        <NewsletterForm />
      </div>

      <div className='mx-auto flex max-w-310 flex-wrap gap-x-12 gap-y-10 px-5 py-14 sm:px-8'>
        <div className='flex min-w-0 flex-[2_1_300px] flex-col gap-4.5'>
          <AwpMarineLogo className='h-10 w-auto self-start brightness-0 invert' />
          <p className='text-on-navy max-w-75 leading-relaxed'>{m.footer_tagline()}</p>
          <div className='flex items-center gap-2.5'>
            <img
              src={Iso9001Img}
              alt={m.footer_iso_9001()}
              loading='lazy'
              className='size-16 rounded-lg bg-white p-1'
            />
            <img
              src={Iso45001Img}
              alt={m.footer_iso_45001()}
              loading='lazy'
              className='size-16 rounded-lg bg-white p-1'
            />
          </div>
          <SocialLinks />
        </div>

        <div className='grid flex-[3_1_480px] grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-[repeat(3,minmax(0,1fr))_auto]'>
          {columns.map((column) => (
            <div key={column.title()} className='flex min-w-0 flex-col gap-3'>
              <h3 className='mb-1 font-bold'>{column.title()}</h3>
              {column.items.map((item) =>
                item.to === '/legal/$slug' ? (
                  <Link
                    key={item.label()}
                    to={item.to}
                    params={{ slug: item.slug }}
                    className='text-on-navy whitespace-nowrap transition-colors hover:text-white'
                  >
                    {item.label()}
                  </Link>
                ) : (
                  <Link
                    key={item.label()}
                    to={item.to}
                    hash={item.hash}
                    className='text-on-navy whitespace-nowrap transition-colors hover:text-white'
                  >
                    {item.label()}
                  </Link>
                ),
              )}
            </div>
          ))}
          <div className='flex min-w-0 flex-col gap-3'>
            <h3 className='mb-1 font-bold'>{m.footer_contact()}</h3>
            {company.phones.map((phone) => (
              <a
                key={phone.tel}
                href={`tel:${phone.tel}`}
                className='text-on-navy whitespace-nowrap transition-colors hover:text-white'
              >
                {phone.display}
              </a>
            ))}
            <a
              href={`mailto:${company.emails.default}`}
              className='text-on-navy whitespace-nowrap transition-colors hover:text-white'
            >
              {company.emails.default}
            </a>
          </div>
        </div>
      </div>

      <div className='mx-auto flex max-w-310 flex-wrap justify-between gap-3 border-t border-[#2c3f5c] px-5 pt-6 pb-10 text-[13px] text-[#8ea0b8] sm:px-8'>
        <p>
          {m.footer_registered({ number: company.registrationNumber, address: company.registeredAddress.formatted })}
        </p>
        <div className='flex flex-wrap items-center gap-x-5 gap-y-2'>
          <LanguageMenu
            side='top'
            className='min-h-9 border-[#3a4c68] px-3 text-[13px] hover:border-white hover:text-white data-popup-open:border-white data-popup-open:text-white'
          />
          <p>
            &copy; {year} {company.legalName}
          </p>
        </div>
      </div>
    </footer>
  );
}
